import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { apiFetch } from '@/api/client';
import { listarCartera, type FilaDeCartera } from '@/api/cartera.api';
import { TarjetaDeProgreso } from '@/components/cartera/TarjetaDeProgreso';
import { VentanaDeFicha } from '@/components/cartera/VentanaDeFicha';
import { useAbrirChat } from '@/hooks/useAbrirChat';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { legible } from '@/lib/cartera';
import { conCuentas, opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { cn } from '@/lib/cn';

/**
 * PERSONAS — ordenadas por quién necesita atención, no alfabéticamente.
 *
 * Mandato: *"personas ordenadas por «necesita atención». Un semáforo marca la
 * temperatura y va con el motivo."*
 *
 * 9-oct, Kervens, con la ficha de progreso de Clientes en la mano: «cuando
 * entro a PERSONAS cada persona debe aparecer con su ficha, no quiero una
 * lista». Cada persona es su tarjeta de progreso (la misma de Clientes: en qué
 * paso va y qué le falta), en el orden de siempre.
 *
 * ═══ EL MOTIVO VA SIEMPRE AL LADO DEL COLOR ═══
 *
 * Un punto rojo sin explicación obliga a abrir la ficha para saber qué pasa.
 * "20 días sin escribir" se entiende sin tocar nada: va encima de la tarjeta.
 */

interface Persona {
  id: string;
  nombre: string;
  telefono: string | null;
  conversationId: string | null;
  diasSinHablar: number | null;
  pendientes: number;
  temperatura: 'ROJO' | 'AMARILLO' | 'VERDE';
  motivo: string;
}

const PUNTO: Record<Persona['temperatura'], string> = {
  ROJO: 'bg-red-500',
  AMARILLO: 'bg-amber-500',
  VERDE: 'bg-emerald-500',
};

// ponytail: sin virtualización, como las tarjetas de Clientes; entra si pasan de unos cientos.
const TANDA = 24;

const SEMAFORO: Array<{ valor: Persona['temperatura']; etiqueta: string }> = [
  { valor: 'ROJO', etiqueta: 'Necesita atención' },
  { valor: 'AMARILLO', etiqueta: 'Pendiente' },
  { valor: 'VERDE', etiqueta: 'Al día' },
];

export default function Personas() {
  const navegar = useNavigate();
  const [ahora] = useState(() => new Date());
  const [cuantas, setCuantas] = useState(TANDA);
  const [elegido, setElegido] = useState<FilaDeCartera | null>(null);
  const chatDe = useAbrirChat();

  const datos = useQuery({
    queryKey: ['personas'],
    queryFn: () => apiFetch<{ personas: Persona[] }>('/api/personas'),
  });
  // La ficha sale de la cartera (misma consulta y misma caché que Clientes).
  const cartera = useQuery({ queryKey: ['cartera'], queryFn: listarCartera });

  const todas = datos.data?.personas ?? [];
  const etapas = cartera.data?.etapas ?? [];
  const fichas = useMemo(() => new Map((cartera.data?.filas ?? []).map((f) => [f.contactId, f])), [cartera.data]);

  // SUPERFILTRO (Josué, 14-oct): semáforo, etapa y pendientes, en la URL.
  const { filtros, ponerVarios } = useFiltrosEnUrl({ buscar: '', atencion: '', etapa: '', pendientes: '' });
  const facetas: Faceta[] = [
    { clave: 'atencion', titulo: 'Atención', todas: 'Todas', opciones: conCuentas(SEMAFORO, todas, (p, v) => p.temperatura === v) },
    { clave: 'etapa', titulo: 'Etapa', todas: 'Todas', opciones: opcionesDe(todas, (p) => fichas.get(p.id)?.etapa, legible) },
    { clave: 'pendientes', titulo: 'Pendientes', opciones: [{ valor: 'si', etiqueta: 'Con pendientes', cuenta: todas.filter((p) => p.pendientes > 0).length }] },
  ];
  const q = filtros.buscar.trim().toLowerCase();
  const lista = todas.filter(
    (p) =>
      (!q || `${p.nombre} ${p.telefono ?? ''}`.toLowerCase().includes(q)) &&
      (!filtros.atencion || p.temperatura === filtros.atencion) &&
      (!filtros.etapa || fichas.get(p.id)?.etapa === filtros.etapa) &&
      (filtros.pendientes !== 'si' || p.pendientes > 0),
  );
  const quedan = lista.length - cuantas;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Personas</h1>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Primero quien necesita tu atención.
        </p>
      </div>

      {datos.isPending || cartera.isPending ? (
        <Spinner />
      ) : datos.isError ? (
        <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
          No se pudieron leer tus personas.
        </p>
      ) : todas.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-400">
          Todavía no hay personas. Aparecen cuando Athena lee tus conversaciones.
        </p>
      ) : (
        <>
          <Superfiltro
            buscar={filtros.buscar}
            onBuscar={(v) => ponerVarios({ buscar: v })}
            placeholder="Buscar por nombre o teléfono…"
            facetas={facetas}
            valores={filtros}
            onCambiar={ponerVarios}
            onLimpiar={() => ponerVarios(sinFiltros(facetas))}
            resultados={textoDeResultados(lista.length, todas.length, ['persona', 'personas'])}
          />
          {lista.length === 0 && (
            <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Nadie coincide con la búsqueda o los filtros.
            </p>
          )}
          <ul className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
            {lista.slice(0, cuantas).map((p) => {
              const fila = fichas.get(p.id);
              return (
                <li key={p.id} className="flex min-w-0 flex-col gap-2">
                  {/* El motivo, siempre junto al color. */}
                  <p className="flex items-center gap-2 px-1 text-xs text-slate-600 dark:text-slate-300">
                    <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', PUNTO[p.temperatura])} />
                    <span className="min-w-0 truncate">{p.motivo}</span>
                  </p>
                  {fila ? (
                    <TarjetaDeProgreso fila={fila} etapas={etapas} ahora={ahora} onVerFicha={() => setElegido(fila)} onAbrirChat={chatDe(fila)} />
                  ) : (
                    // Sin ficha en la cartera (o la cartera no cargó): al menos se llega a la persona.
                    <button
                      type="button"
                      onClick={() => navegar(`/personas/${p.id}`)}
                      className="flex min-h-[60px] w-full items-center gap-3 rounded-2xl border border-surface-border bg-white px-4 py-3 text-left dark:border-slate-800 dark:bg-slate-900"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 dark:text-slate-100">{p.nombre}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {quedan > 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setCuantas((c) => c + TANDA)}
                className="rounded-md border border-surface-border bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Ver {Math.min(TANDA, quedan)} más
              </button>
            </div>
          )}
        </>
      )}

      {elegido && <VentanaDeFicha key={elegido.contactId} fila={elegido} etapas={etapas} abierta onCerrar={() => setElegido(null)} />}
    </div>
  );
}
